import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Header, Onboarding, toast, useModal } from '@ohif/ui-next';
import { SidebarNavigation } from '@ohif/ui';
import { useAppConfig } from '@state';
import PropTypes from 'prop-types';

// Importaciones de servicios
import { fetchAllBranches } from '../Branches/branch'; // Necesario para el selector de sucursal
import { fetchAllModalities } from './services'; // Necesario para el selector de modalidad
import { fetchAllEquipments } from './services'; // Necesario para el selector de equipos
import {
  fetchAllServices,
  createService,
  updateService,
  deleteService,
  ServiceAPI,
  ServiceData,
  ModalityAPI, // Para el selector
  EquipmentAPI, // Para el selector
} from './services';
import { getHeaderConfig } from '../Users/Users';
import ServiceFormModal from './components/ServiceFormModal';
import ServiceList from './components/ServiceList';

// --- Estado Inicial del Formulario ---
const INITIAL_FORM_DATA: ServiceData = {
  fk_branch: '',
  fk_modality: '',
  fk_equipments: [],
  name: '',
  status: true,
  calendar_settings: {
    default_duration_min: 30,
    max_patients_per_slot: 1,
  },
};

function ServicePanel({ servicesManager }) {
  const [services, setServices] = useState<ServiceAPI[]>([]);
  const [branches, setBranches] = useState([]);
  const [modalities, setModalities] = useState<ModalityAPI[]>([]);
  const [equipments, setEquipments] = useState<EquipmentAPI[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<ServiceAPI | null>(null);
  const [formData, setFormData] = useState<ServiceData>(INITIAL_FORM_DATA);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { show } = useModal();
  const [appConfig] = useAppConfig();
  const { menuOptions } = getHeaderConfig({ servicesManager, t, show, navigate, appConfig });

  // --- Lógica de Carga de Datos ---
  const loadDependencies = async () => {
    try {
      // Cargar listas para selectores
      const [branchList, modalityList, equipmentList] = await Promise.all([
        fetchAllBranches(),
        fetchAllModalities(),
        fetchAllEquipments(),
      ]);
      setBranches(branchList);
      setModalities(modalityList);
      setEquipments(equipmentList);
    } catch (error) {
      toast.error('Error al cargar dependencias (sucursales/modalidades/equipos).');
    }
  };

  const loadServices = async () => {
    try {
      setIsLoading(true);
      const apiServices = await fetchAllServices();
      setServices(apiServices);
    } catch (error) {
      toast.error('Error al obtener servicios: ' + (error as any).message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDependencies();
    loadServices();
  }, []);

  // --- Lógica de Filtro ---
  const filteredServices = services.filter(
    (service) =>
      service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.fk_branch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.fk_modality.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // --- Manejadores CRUD (Similar a los anteriores) ---

  const handleCreateService = async () => {
    try {
      const response = await createService(formData);
      toast.success(`Servicio ${response.name} creado exitosamente.`);
      await loadServices();
      resetForm();
    } catch (err: any) {
      toast.error('Error al crear servicio: ' + (err.message || err));
    }
  };

  const handleUpdateService = async () => {
    if (!editingService) return;
    try {
      // Asegúrate de que los IDs no cambien si no deben
      const updateData: Partial<ServiceData> = {
        name: formData.name,
        status: formData.status,
        fk_equipments: formData.fk_equipments,
        calendar_settings: formData.calendar_settings,
        // No permitimos cambiar branch ni modality al editar
      };

      const response = await updateService(editingService._id, updateData);
      toast.success(`Servicio ${response.name} actualizado.`);
      await loadServices();
      resetForm();
    } catch (err: any) {
      toast.error('Error al actualizar servicio: ' + (err.message || err));
    }
  };

  const handleDeleteService = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este servicio?')) return;
    try {
      await deleteService(id);
      toast.success('Servicio eliminado correctamente.');
      setServices((prev) => prev.filter((s) => s._id !== id));
    } catch (err: any) {
      toast.error('Error al eliminar el servicio: ' + (err.message || err));
    }
  };

  const handleEditService = (service: ServiceAPI) => {
    setEditingService(service);
    // Mapear ServiceAPI a ServiceData para el formulario
    setFormData({
      fk_branch: service.fk_branch._id,
      fk_modality: service.fk_modality._id,
      fk_equipments: service.fk_equipments.map(e => e._id), // Extraer solo los IDs
      name: service.name,
      status: service.status,
      calendar_settings: service.calendar_settings,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setEditingService(null);
    setIsDialogOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    editingService ? handleUpdateService() : handleCreateService();
  };

  return (
    <div className="flex h-full flex-col bg-black">
      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={false}
        WhiteLabeling={appConfig.whiteLabeling}
      />
      <Onboarding />
      <SidebarNavigation onFilterToggle={() => navigate('/')} />

      <div className="container mx-auto min-h-screen bg-gray-50 pt-5 text-white max-sm:p-4 sm:pl-9">
        {/* Header del Panel */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Gestión de Servicios</h1>
            <p className="mt-1">Define los tipos de estudio y la configuración de calendario.</p>
          </div>
          <button
            onClick={() => {
              setEditingService(null);
              setFormData(INITIAL_FORM_DATA);
              setIsDialogOpen(true);
            }}
            className="bg-secondary-dark hover:bg-primary-dark flex items-center rounded px-4 py-2 text-white"
          >
            Crear Servicio
          </button>
        </div>

        {/* Lista de Servicios */}
        <ServiceList
          services={filteredServices}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isLoading={isLoading}
          handleEditService={handleEditService}
          handleDeleteService={handleDeleteService}
        />
      </div>

      {/* Modal de Formulario */}
      <ServiceFormModal
        isDialogOpen={isDialogOpen}
        editingService={editingService}
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        resetForm={resetForm}
        branches={branches}
        modalities={modalities}
        equipments={equipments}
      />
    </div>
  );
}

ServicePanel.propTypes = {
  servicesManager: PropTypes.object.isRequired,
};

export default ServicePanel;
